import React from 'react';
import {
    BringzzIcon,
    BringzzText
} from '@bringzz/components';
import { useBasket } from 'context/BasketContext';
import { useTranslation } from 'react-i18next';
import { DELIVERY_TYPES, getDeliveryTypeLabel } from 'utils/constants';

/**
 * SubmittingOrderOverlay - Displays a loading overlay while waiting for payment confirmation
 *
 * Payment Flow:
 * 1. User completes Stripe payment on PaymentPage
 * 2. PaymentPage shows this overlay
 * 3. Stripe sends webhook to backend with payment confirmation
 * 4. Backend (PaymentModel.php) creates ORDER_PAID notification via NotificationModel
 * 5. NotificationSender sends to socket server at https://172.30.0.6:8443/send-notification
 * 6. Socket server emits 'refetchData' event with ORDER_PAID to the customer
 * 7. PaymentPage receives ORDER_PAID via socket and navigates to next step
 *
 * Note: Currently the backend sends "Payment confirmed" as a string in notificationContent,
 * not an object with orderId. The PaymentPage component handles the orderId validation.
 */
const SubmittingOrderOverlay = ({ deliveryType = 0, pharmacy, orderId }) => {
    const { getBasketTotal, getBasketSubTotal, getFees } = useBasket()
    const { t } = useTranslation();

    return <div className="fixed inset-0 bg-black/75 flex items-center justify-center z-50">
        <div className="h-full w-full text-white max-w-md">
        <div className='p-8'>
            <div className='mb-2 flex items-center justify-between'>
                <BringzzText tag='h1' className='text-white'>Confirming payment...</BringzzText>
                <div style={{ animation: 'spin 4s linear infinite' }}>
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 2V6M12 18V22M6 12H2M22 12H18M19.0784 19.0784L16.25 16.25M19.0784 4.99994L16.25 7.82837M4.92157 19.0784L7.75 16.25M4.92157 4.99994L7.75 7.82837" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </div>
            </div>
            {(deliveryType == DELIVERY_TYPES.BRINGZZ_PICKUP || deliveryType == DELIVERY_TYPES.MAIL_DELIVERY) && <div className='flex items-center justify-between py-2 px-2'>
                <BringzzIcon size='26' icon='IconMapMarker' folder={'LineIcons'} />
                <div className='flex flex-col pr-8'>
                    <BringzzText tag='h3' className='text-white'>Deliver to</BringzzText>
                    <div className='w-64'>
                        <BringzzText
                            tag='h3'
                            className='text-normal text-white whitespace-pre-line truncate'
                        >
                            Home
                        </BringzzText>
                    </div>
                </div>
            </div>}
            <div className='flex items-center justify-between py-2 px-2'>
                <BringzzIcon size='22' icon='IconPackage' folder={'LineIcons'} />
                <div className='flex flex-col pr-8'>
                    <BringzzText tag='h3' className='text-white'>Delivery type</BringzzText>
                    <div className='w-64'>
                        <BringzzText
                            tag='h3'
                            className='text-normal text-white whitespace-pre-line truncate'
                        >
                            {getDeliveryTypeLabel(deliveryType, t)}
                        </BringzzText>
                    </div>
                </div>
            </div>
            <div className='flex items-center justify-between py-2 px-2'>
                <div>
                    <BringzzIcon
                        size='28'
                        icon='IconPharmacy'
                        folder={'HealthIcons'}
                    />
                </div>
                <div className='flex flex-col pr-8'>
                    <BringzzText tag='h3' className='text-white'>Pharmacy (BEST FIT)</BringzzText>
                    <div className='w-64'>
                        <BringzzText
                            tag='h3'
                            className='text-normal text-white whitespace-pre-line truncate'
                        >
                            {pharmacy.pharmacy_name || "pharmacy_name"}
                        </BringzzText>
                    </div>
                </div>
            </div>
            <div className='my-2 text-white'><hr></hr></div>
            <div className='flex py-2 px-2 '>
                <div className='mr-8'>
                    <BringzzIcon
                        size='24'
                        icon='IconTicketAlt'
                        folder={'LineIcons'}
                    />
                </div>
                <div className='flex flex-col w-full'>
                    <BringzzText tag='h3' className='text-white'>Order Summary</BringzzText>
                    <div className='w-full mt-1'>
                        <div className='flex justify-between w-full'>
                            <BringzzText
                                tag='h4'
                                className='font-normal text-white whitespace-pre-line truncate'
                            >
                                Subtotal
                            </BringzzText>
                            <BringzzText
                                tag='h4'
                                className='font-normal text-white whitespace-pre-line truncate'
                            >
                                € {getBasketSubTotal()}
                            </BringzzText>
                        </div>
                        <div className='flex justify-between w-full'>
                            <div className='flex items-center space-x-1'>
                                <BringzzText
                                    tag='h4'
                                    className='font-normal text-white whitespace-pre-line truncate'
                                >
                                    Delivery fee & handling
                                </BringzzText>
                                <BringzzIcon
                                    icon="IconQuestionCircle"
                                    folder="LineIcons"
                                    className="h-4 w-4 cursor-pointer text-white"
                                    size="18"
                                ></BringzzIcon>
                            </div>
                            <BringzzText
                                tag='h4'
                                className='font-normal text-white whitespace-pre-line truncate'
                            >
                                € {getFees()}
                            </BringzzText>
                        </div>
                        <div className='flex justify-between w-full'>
                            <BringzzText
                                tag='h4'
                                className='font-bold text-white whitespace-pre-line truncate'
                            >
                                Total
                            </BringzzText>
                            <BringzzText
                                tag='h4'
                                className='font-bold text-white whitespace-pre-line truncate'
                            >
                                € {getBasketTotal()}
                            </BringzzText>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        </div>
    </div>
}

export default SubmittingOrderOverlay;